// Reading the client's boundary files in the browser (nothing is uploaded until they
// become zones): KML and KMZ (Google Earth) and GeoJSON. Closed areas can become zones;
// lines (pipelines, roads) and points (survey marks) are listed but skipped.

export type LonLat = [number, number];

export interface Shape {
  name: string;
  kind: 'area' | 'line' | 'point';
  /** Areas: the outline, not closed (first point isn't repeated). */
  points: LonLat[];
}

const MAX_POINTS = 200;

const valid = (p: LonLat) => Number.isFinite(p[0]) && Number.isFinite(p[1]) && Math.abs(p[0]) <= 180 && Math.abs(p[1]) <= 90;

function openRing(pts: LonLat[]): LonLat[] {
  const out = pts.filter(valid);
  if (out.length > 1 && out[0][0] === out[out.length - 1][0] && out[0][1] === out[out.length - 1][1]) out.pop();
  return out;
}

/** Fewer points, same shape (Douglas-Peucker), so outlines stay at most 200 points. */
export function simplify(pts: LonLat[], max = MAX_POINTS): LonLat[] {
  if (pts.length <= max) return pts;
  const dist = (p: LonLat, a: LonLat, b: LonLat) => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = dx * dx + dy * dy;
    const t = len ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len)) : 0;
    return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
  };
  const run = (tol: number): LonLat[] => {
    const keep = new Uint8Array(pts.length);
    keep[0] = keep[pts.length - 1] = 1;
    const stack: [number, number][] = [[0, pts.length - 1]];
    while (stack.length) {
      const [a, b] = stack.pop() as [number, number];
      let best = -1;
      let far = tol;
      for (let i = a + 1; i < b; i++) {
        const d = dist(pts[i], pts[a], pts[b]);
        if (d > far) {
          far = d;
          best = i;
        }
      }
      if (best >= 0) {
        keep[best] = 1;
        stack.push([a, best], [best, b]);
      }
    }
    return pts.filter((_, i) => keep[i]);
  };
  let tol = 1e-6;
  let out = run(tol);
  while (out.length > max) {
    tol *= 2;
    out = run(tol);
  }
  return out;
}

// ---- KML / KMZ ----

function kmlCoords(text: string): LonLat[] {
  return text
    .trim()
    .split(/\s+/)
    .map((t) => t.split(',').map(Number))
    .filter((c) => c.length >= 2)
    .map((c) => [c[0], c[1]] as LonLat);
}

export function readKml(xml: string): Shape[] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new Error("This file isn't readable KML.");
  const shapes: Shape[] = [];
  const marks = Array.from(doc.getElementsByTagNameNS('*', 'Placemark'));
  marks.forEach((pm, i) => {
    const name = pm.getElementsByTagNameNS('*', 'name')[0]?.textContent?.trim() || `Shape ${i + 1}`;
    const polys = Array.from(pm.getElementsByTagNameNS('*', 'Polygon'));
    polys.forEach((poly, j) => {
      const ring = poly.getElementsByTagNameNS('*', 'outerBoundaryIs')[0]?.getElementsByTagNameNS('*', 'coordinates')[0]?.textContent ?? '';
      const pts = openRing(kmlCoords(ring));
      if (pts.length >= 3) shapes.push({ name: polys.length > 1 ? `${name} (${j + 1})` : name, kind: 'area', points: simplify(pts) });
    });
    for (const line of Array.from(pm.getElementsByTagNameNS('*', 'LineString'))) {
      shapes.push({ name, kind: 'line', points: kmlCoords(line.getElementsByTagNameNS('*', 'coordinates')[0]?.textContent ?? '').filter(valid) });
    }
    for (const pt of Array.from(pm.getElementsByTagNameNS('*', 'Point'))) {
      shapes.push({ name, kind: 'point', points: kmlCoords(pt.getElementsByTagNameNS('*', 'coordinates')[0]?.textContent ?? '').filter(valid) });
    }
  });
  return shapes;
}

/** The .kml inside a .kmz (a zip file). */
export async function kmlFromKmz(bytes: Uint8Array): Promise<string> {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i--) {
    if (dv.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("This KMZ file isn't readable.");
  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  for (let n = 0; n < count; n++) {
    if (dv.getUint32(p, true) !== 0x02014b50) break;
    const method = dv.getUint16(p + 10, true);
    const size = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const local = dv.getUint32(p + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    if (!/\.kml$/i.test(name)) continue;
    const start = local + 30 + dv.getUint16(local + 26, true) + dv.getUint16(local + 28, true);
    const data = bytes.slice(start, start + size);
    if (method === 0) return new TextDecoder().decode(data);
    if (method === 8) {
      const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
      return await new Response(stream).text();
    }
    throw new Error('This KMZ file uses a kind of compression the console can’t read.');
  }
  throw new Error('There’s no KML inside this KMZ file.');
}

// ---- GeoJSON ----

type Geo = { type: string; coordinates?: unknown; geometries?: Geo[]; features?: Geo[]; geometry?: Geo | null; properties?: Record<string, unknown> | null };

export function readGeoJson(text: string): Shape[] {
  let root: Geo;
  try {
    root = JSON.parse(text);
  } catch {
    throw new Error("This file isn't readable GeoJSON.");
  }
  const shapes: Shape[] = [];
  const add = (g: Geo | null | undefined, name: string) => {
    if (!g) return;
    const c = g.coordinates as never;
    switch (g.type) {
      case 'Polygon': {
        const pts = openRing(((c as LonLat[][])[0] ?? []).map((p) => [p[0], p[1]] as LonLat));
        if (pts.length >= 3) shapes.push({ name, kind: 'area', points: simplify(pts) });
        break;
      }
      case 'MultiPolygon':
        (c as LonLat[][][]).forEach((poly, i) => add({ type: 'Polygon', coordinates: poly }, `${name} (${i + 1})`));
        break;
      case 'LineString':
        shapes.push({ name, kind: 'line', points: (c as LonLat[]).map((p) => [p[0], p[1]] as LonLat) });
        break;
      case 'MultiLineString':
        (c as LonLat[][]).forEach((l) => shapes.push({ name, kind: 'line', points: l.map((p) => [p[0], p[1]] as LonLat) }));
        break;
      case 'Point':
        shapes.push({ name, kind: 'point', points: [[(c as number[])[0], (c as number[])[1]]] });
        break;
      case 'MultiPoint':
        (c as LonLat[]).forEach((p) => shapes.push({ name, kind: 'point', points: [[p[0], p[1]]] }));
        break;
      case 'GeometryCollection':
        (g.geometries ?? []).forEach((x) => add(x, name));
        break;
    }
  };
  const nameOf = (props: Record<string, unknown> | null | undefined, i: number) => {
    for (const k of ['name', 'Name', 'NAME', 'title', 'label', 'id']) if (props && typeof props[k] === 'string' && (props[k] as string).trim()) return (props[k] as string).trim();
    return `Shape ${i + 1}`;
  };
  if (root.type === 'FeatureCollection') (root.features ?? []).forEach((f, i) => add(f.geometry, nameOf(f.properties, i)));
  else if (root.type === 'Feature') add(root.geometry, nameOf(root.properties, 0));
  else add(root, 'Shape 1');
  return shapes;
}

/** Reads a boundary file by its name: .kml, .kmz, .geojson or .json. */
export async function readBoundaryFile(file: File): Promise<Shape[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.kmz')) return readKml(await kmlFromKmz(new Uint8Array(await file.arrayBuffer())));
  if (name.endsWith('.kml')) return readKml(await file.text());
  if (name.endsWith('.geojson') || name.endsWith('.json')) return readGeoJson(await file.text());
  if (name.endsWith('.zip') || name.endsWith('.shp')) throw new Error('Shapefiles are coming next. For now, ask for the file as KML/KMZ (Google Earth) or GeoJSON.');
  throw new Error('This kind of file can’t be read. Use KML or KMZ (Google Earth) or GeoJSON.');
}

/** "lat, lon" per line to outline points. */
export function readTypedPoints(text: string): { points: LonLat[]; problem: string | null } {
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  const points: LonLat[] = [];
  for (const [i, l] of lines.entries()) {
    const m = l.match(/^(-?\d{1,2}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
    if (!m) return { points: [], problem: `Line ${i + 1} isn't “latitude, longitude”.` };
    const p: LonLat = [Number(m[2]), Number(m[1])];
    if (!valid(p)) return { points: [], problem: `Line ${i + 1} is outside the world.` };
    points.push(p);
  }
  if (points.length < 3) return { points: [], problem: 'A zone needs at least 3 corners.' };
  return { points: openRing(points), problem: null };
}
